import React from 'react';
import { Col, Row } from '../../lib/utils';

/** 수업마다 따로 정할 수 있는 예약 규칙 — 서버는 분 단위로 받고, null 이면 센터 기본값을 따른다 */
export const RULE_KEYS = ['bookingOpenDeadline', 'cancellationDeadline', 'closingDeadline', 'waitlistAutoDeadline'] as const;
export type RuleKey = (typeof RULE_KEYS)[number];
export type RuleSource = Partial<Record<RuleKey, number | null>>;
type RuleState = { custom: boolean; minutes: Record<RuleKey, number> };
/**
 * base = 서버에 저장돼 있는 상태(화면을 열 때, 저장에 성공할 때 갱신). 사용자가 손댄 규칙만 저장하려고 들고 있는다.
 * stored = 실제로 값이 저장돼 있는 규칙. 빈 칸은 센터 값으로 채워 보여 줄 뿐이라, 끌 때 지울 대상이 아니다.
 */
type BaseState = RuleState & { stored: Record<RuleKey, boolean> };
export type RuleFieldsValue = RuleState & { base: BaseState };
/** 센터 예약 설정 — 기본값 표시와, 이 수업 규칙이 실제로 적용되는지 안내하는 데 쓴다 */
export type CenterRules = RuleSource & { autoClosingEnabled?: boolean; weeklyReleaseEnabled?: boolean };

// 센터 설정도 비어 있을 때 서버가 쓰는 값(getEffectiveRules)과 같다
const FALLBACK_MINUTES: Record<RuleKey, number> = {
    bookingOpenDeadline: 20160,
    cancellationDeadline: 480,
    closingDeadline: 480,
    waitlistAutoDeadline: 60,
};

const FIELDS: { key: RuleKey; label: string; unit: 'day' | 'hour'; hint: string }[] = [
    { key: 'bookingOpenDeadline', label: '예약 오픈', unit: 'day', hint: '수업 며칠 전부터 예약을 받을지' },
    { key: 'cancellationDeadline', label: '취소 마감', unit: 'hour', hint: '이 시간이 지나 취소하면 늦은 취소로 회차가 차감돼요' },
    { key: 'closingDeadline', label: '폐강 기준', unit: 'hour', hint: '이 시점에 최소 인원이 안 차면 폐강돼요' },
    { key: 'waitlistAutoDeadline', label: '대기 자동 확정', unit: 'hour', hint: '이 시간 전까지만 대기자를 자동으로 확정해요' },
];

const seedMinutes = (original: RuleSource, centerRules?: RuleSource | null) =>
    RULE_KEYS.reduce((acc, key) => {
        acc[key] = original[key] ?? centerRules?.[key] ?? FALLBACK_MINUTES[key];
        return acc;
    }, {} as Record<RuleKey, number>);

/** 수정 화면을 열 때의 값 — 규칙이 하나라도 저장돼 있으면 '따로 정함'으로 시작한다 */
export const initRuleFields = (original: RuleSource, centerRules?: RuleSource | null): RuleFieldsValue => {
    const stored = RULE_KEYS.reduce((acc, key) => {
        acc[key] = original[key] !== undefined && original[key] !== null;
        return acc;
    }, {} as Record<RuleKey, boolean>);
    const state: RuleState = {
        custom: RULE_KEYS.some((key) => stored[key]),
        minutes: seedMinutes(original, centerRules),
    };
    return { ...state, base: { ...state, stored } };
};

/**
 * 사용자가 바꾼 규칙만 돌려준다. 센터 기본으로 되돌리면 null 을 보낸다.
 * 저장된 값이 일부만 있는 수업은 빈 칸을 센터 값으로 채워 보여 주는데, 그 채운 값까지 보내면
 * 다른 설정만 고쳐도 센터 값이 수업에 박혀 이후 센터 설정 변경을 따라가지 않게 된다.
 * 비교 기준은 base 하나다 — 저장 직후 목록이 다시 오기 전 원본(props)과 비교하면 되돌린 값이 빠진다.
 */
export const diffRuleFields = (value: RuleFieldsValue): RuleSource => {
    const changed: RuleSource = {};
    RULE_KEYS.forEach((key) => {
        if (!value.custom) {
            // 센터 기본으로 되돌림 — 실제로 저장돼 있던 규칙만 지운다(없던 규칙까지 null 을 보내면 회차별 설정이 지워진다)
            if (value.base.stored[key]) changed[key] = null;
            return;
        }
        // 이미 따로 정해 둔 수업에서 손대지 않은 칸은 보내지 않는다.
        // 새로 켠 경우는 보이는 네 값을 모두 이 수업의 규칙으로 정하는 것이다.
        if (value.base.custom && value.minutes[key] === value.base.minutes[key]) return;
        changed[key] = value.minutes[key];
    });
    return changed;
};

/**
 * 센터 설정이 늦게 오면 저장 안 된 칸이 임시 기본값으로 채워져 있다 — 센터 값이 오면 그 칸만 다시 채운다.
 * 기준(base)도 같이 바꿔야 '센터 값으로 정했는데 변경분이 없다'가 생기지 않는다. 센터 설정 전에는 입력을 막아 두므로 사용자가 고친 값은 없다.
 */
export const reseedRuleFields = (value: RuleFieldsValue, centerRules: RuleSource): RuleFieldsValue => {
    const minutes = { ...value.minutes };
    const baseMinutes = { ...value.base.minutes };
    RULE_KEYS.forEach((key) => {
        if (value.base.stored[key] || value.minutes[key] !== value.base.minutes[key]) return;
        minutes[key] = baseMinutes[key] = centerRules[key] ?? FALLBACK_MINUTES[key];
    });
    return { ...value, minutes, base: { ...value.base, minutes: baseMinutes } };
};

/**
 * 저장에 성공하면 '보낸 값'을 새 기준으로 삼는다. 화면 값은 그대로 둔다 —
 * 저장 중에 더 고친 내용이 있으면 그것만 다음 저장의 변경분으로 남는다.
 * 보내지 않은 칸의 기준은 지금 기준을 유지한다 — 저장 중에 센터 설정이 와서 다시 채운 값을 저장 당시 임시값으로 되돌리지 않게.
 */
export const commitRuleFields = (value: RuleFieldsValue, saved: RuleFieldsValue): RuleFieldsValue => {
    const sent = diffRuleFields(saved);
    const stored = RULE_KEYS.reduce((acc, key) => {
        acc[key] = saved.custom && (saved.base.stored[key] || key in sent);
        return acc;
    }, {} as Record<RuleKey, boolean>);
    const minutes = RULE_KEYS.reduce((acc, key) => {
        acc[key] = key in sent ? saved.minutes[key] : value.base.minutes[key];
        return acc;
    }, {} as Record<RuleKey, number>);
    return { ...value, base: { custom: saved.custom, minutes, stored } };
};

/**
 * 방금 저장한 규칙 기억 — 저장 뒤 목록·상세 재조회가 끝나기 전에 화면을 닫았다 다시 열면 캐시가 옛 값이라,
 * 그걸 기준으로 삼으면 되돌린 값이 변경분에서 빠진다. 저장 시각보다 나중에 받은 데이터가 오면 더는 쓰지 않는다.
 * 화면(팝오버)이 닫혀도 남아야 해서 모듈에 둔다. 키는 'template:<id>' / 'session:<id>'.
 */
const savedRules = new Map<string, { rules: RuleSource; savedAt: number }>();

export const rememberSavedRules = (key: string, rules: RuleSource) => {
    if (Object.keys(rules).length === 0) return;
    const prev = savedRules.get(key)?.rules ?? {};
    savedRules.set(key, { rules: { ...prev, ...rules }, savedAt: Date.now() });
};

/** dataUpdatedAt = 원본을 받아 온 시각(React Query). 저장보다 오래된 원본이면 저장값을 덮어 돌려준다 */
export const withSavedRules = (key: string, original: RuleSource, dataUpdatedAt: number): RuleSource => {
    const saved = savedRules.get(key);
    if (!saved) return original;
    if (dataUpdatedAt > saved.savedAt) {
        savedRules.delete(key);
        return original;
    }
    return { ...original, ...saved.rules };
};

const toDisplay = (minutes: number, unit: 'day' | 'hour') => (unit === 'day' ? minutes / 1440 : minutes / 60);
// 서버 상한(366일)과 같다 — 넘기면 저장이 400 으로 실패한다
const RULE_MINUTES_MAX = 366 * 24 * 60;
const toMinutes = (display: number, unit: 'day' | 'hour') =>
    Math.min(RULE_MINUTES_MAX, unit === 'day' ? Math.round(display) * 1440 : Math.round(display * 60));
const formatDisplay = (minutes: number, unit: 'day' | 'hour') => {
    const n = Math.round(toDisplay(minutes, unit) * 10) / 10;
    return unit === 'day' ? `${n}일 전` : `${n}시간 전`;
};

/**
 * 예약 규칙 입력 — 센터 기본 설정을 따를지, 이 수업만 따로 정할지.
 * 값은 분 단위로 들고 있고 화면에만 일·시간으로 보여 준다(되돌려 저장할 때 반올림 오차가 생기지 않게).
 */
const GroupRuleFields = ({
    value,
    centerRules,
    onChange,
    inputStyle,
}: {
    value: RuleFieldsValue;
    centerRules?: CenterRules | null;
    onChange: (next: RuleFieldsValue) => void;
    inputStyle: React.CSSProperties;
}) => {
    // 센터 설정 때문에 이 값이 실제로 쓰이지 않는 경우를 알려 준다(서버: 주간 공개면 예약 오픈 무시, 자동 폐강 꺼짐이면 폐강 없음)
    const inactiveHint = (key: RuleKey): string | null => {
        if (key === 'bookingOpenDeadline' && centerRules?.weeklyReleaseEnabled === true) {
            return '주간 일괄 공개를 쓰는 센터라 적용되지 않아요';
        }
        if (key === 'closingDeadline' && centerRules?.autoClosingEnabled === false) {
            return '자동 폐강이 꺼져 있어 적용되지 않아요';
        }
        // 폐강 배치는 시작 전 수업만 보므로 0시간이면 폐강 시각이 오지 않는다
        if (key === 'closingDeadline' && value.minutes.closingDeadline === 0) {
            return '0시간이면 자동 폐강되지 않아요';
        }
        return null;
    };

    const centerReady = !!centerRules;

    const toggle = () => {
        if (!centerReady) return;
        // 켤 때는 저장된 값 → 센터 값 순으로 채워, 화면에 보이는 숫자가 지금 실제로 적용 중인 규칙이 되게 한다.
        // 저장된 값은 원본(props)이 아니라 기준(base)에서 읽는다 — 저장 직후 재조회 전이면 원본은 옛 값이다
        if (!value.custom) {
            const minutes = RULE_KEYS.reduce((acc, key) => {
                acc[key] = value.base.stored[key] ? value.base.minutes[key] : centerRules?.[key] ?? FALLBACK_MINUTES[key];
                return acc;
            }, {} as Record<RuleKey, number>);
            onChange({ ...value, custom: true, minutes });
        }
        else onChange({ ...value, custom: false });
    };

    return (
        <Col style={{ gap: 8, padding: '12px 14px', backgroundColor: '#F9FAFB', borderRadius: 8, border: '1px solid #E5E7EB' }}>
            <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>예약 규칙</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: centerReady ? 'pointer' : 'default', fontSize: 12, color: centerReady ? '#374151' : '#9CA3AF' }}>
                    {/* 센터 설정을 받기 전에 켜면 빈 칸이 임시 기본값으로 채워져 그대로 저장된다 */}
                    <input type="checkbox" checked={value.custom} onChange={toggle} disabled={!centerReady} />
                    {centerReady ? '이 수업만 따로 정하기' : '센터 설정 불러오는 중…'}
                </label>
            </Row>

            {!value.custom && (
                <span style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.6 }}>
                    센터 기본 설정(그룹수업 예약 설정)을 따라요
                    {centerRules && (
                        <>
                            {' · '}
                            {FIELDS.map((f) => `${f.label} ${formatDisplay(centerRules[f.key] ?? FALLBACK_MINUTES[f.key], f.unit)}`).join(' · ')}
                        </>
                    )}
                </span>
            )}

            {value.custom &&
                FIELDS.map((f) => (
                    <Row key={f.key} style={{ alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                        <Col style={{ gap: 2 }}>
                            <span style={{ fontSize: 13, color: '#374151' }}>{f.label}</span>
                            <span style={{ fontSize: 11, color: inactiveHint(f.key) ? '#B45309' : '#9CA3AF' }}>{inactiveHint(f.key) ?? f.hint}</span>
                        </Col>
                        <Row style={{ alignItems: 'center', gap: 6, flexShrink: 0 }}>
                            <input
                                type="number"
                                min={0}
                                step={f.unit === 'day' ? 1 : 0.5}
                                // 센터 설정 전에는 빈 칸이 임시 기본값이라 그걸 기준으로 고치면 엉뚱한 값이 저장된다
                                disabled={!centerReady}
                                value={toDisplay(value.minutes[f.key], f.unit)}
                                onChange={(e) => {
                                    const n = Math.max(0, Number(e.target.value) || 0);
                                    onChange({ ...value, minutes: { ...value.minutes, [f.key]: toMinutes(n, f.unit) } });
                                }}
                                style={{ ...inputStyle, width: 72, textAlign: 'right' }}
                            />
                            <span style={{ fontSize: 13, color: '#6B7280', width: 44 }}>{f.unit === 'day' ? '일 전' : '시간 전'}</span>
                        </Row>
                    </Row>
                ))}
        </Col>
    );
};

export default GroupRuleFields;
